import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { ReportStatus } from '@repo/shared/types';
import { Button } from '@repo/ui/button';
import { Skeleton } from '@repo/ui/skeleton';
import { AlertCircleIcon, ArrowLeftIcon } from 'lucide-react';
import { useCallback } from 'react';
import { useParams } from 'react-router';
import { useONavigate } from '@/hooks/use-organization-navigate';
import { trpc } from '@/providers/trpc';
import { ComplianceReviewView } from './components/compliance-review';
import { ProcessingView } from './components/processing';
import { ReportPolicyDetails } from './components/report-policy-details';

const ReportDetailSkeleton = () => {
  useLocale();
  return (
    <div className="raven-page space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
};

export const ReportIdPage = () => {
  useLocale();

  const { reportId } = useParams<{ reportId: string }>();
  const oNavigate = useONavigate();

  const {
    data: report,
    isLoading,
    isError,
    refetch,
  } = trpc.report.getReport.useQuery({ reportId: reportId ?? '' }, { enabled: Boolean(reportId) });

  const handleBack = useCallback(() => {
    oNavigate({ to: '/reports' });
  }, [oNavigate]);

  const handleProcessingCompleted = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (!reportId) {
    return null;
  }

  if (isLoading) {
    return <ReportDetailSkeleton />;
  }

  if (isError || !report) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
        <AlertCircleIcon className="h-12 w-12 text-muted-foreground" />
        <div className="text-center">
          <h2 className="font-semibold text-lg">{t('auth.reportUnavailable')}</h2>
          <p className="text-muted-foreground text-sm">
            {t('auth.thisReportMayStillBeGeneratingOrIsUnavailable')}{' '}
          </p>
        </div>
        <Button onClick={handleBack} variant="outline">
          <ArrowLeftIcon className="me-2 h-4 w-4 rtl:rotate-180" />
          {t('auth.backToReports')}{' '}
        </Button>
      </div>
    );
  }

  const status = report.status;

  return (
    <div className="raven-page space-y-6">
      {/* Breadcrumb-style back button */}
      <div className="flex items-center gap-2">
        <Button onClick={handleBack} size="sm" variant="ghost">
          <ArrowLeftIcon className="me-1 h-4 w-4 rtl:rotate-180" />
          {t('workspace.reports')}{' '}
        </Button>
        <span className="text-muted-foreground">/</span>
        <span className="font-medium text-sm">{report.reportName ?? reportId}</span>
      </div>

      <ReportPolicyDetails report={report} />

      {/* Status-based view routing */}
      {status === ReportStatus.PROCESSING && (
        <ProcessingView onCompleted={handleProcessingCompleted} reportId={reportId} />
      )}

      {[ReportStatus.FINISHED, ReportStatus.PARTIAL, ReportStatus.FAILED].some(
        (value) => value === status,
      ) && <ComplianceReviewView report={report} />}

      {status === ReportStatus.DRAFT && (
        <div className="flex flex-col items-center gap-4 p-8 text-center text-muted-foreground">
          <p>{t('auth.thisReportIsStillInDraftStateSubmitIt')}</p>
          <Button onClick={() => oNavigate({ to: `/reports/create/${reportId}` })}>
            {t('auth.continueToWizard')}{' '}
          </Button>
        </div>
      )}
    </div>
  );
};
